/**
 * Configuración global de SweetAlert2
 * Para uso en todo el proyecto
 */

import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// Configuración por defecto para todos los modales
const defaultConfig = {
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Aceptar',
    cancelButtonText: 'Cancelar',
    allowOutsideClick: false,
    allowEscapeKey: true,
    showClass: {
        popup: 'animate__animated animate__fadeInDown animate__faster'
    },
    hideClass: {
        popup: 'animate__animated animate__fadeOutUp animate__faster'
    }
};

// Mezclar configuración por defecto con SweetAlert2
const CustomSwal = Swal.mixin(defaultConfig);

/**
 * Funciones personalizadas de SweetAlert2
 */
export const AlertManager = {
    
    /**
     * Alerta de éxito
     */
    success(title, text = '', options = {}) {
        return CustomSwal.fire({
            icon: 'success',
            title: title,
            text: text,
            ...options
        });
    },
    
    /**
     * Alerta de error
     */
    error(title, text = '', options = {}) {
        return CustomSwal.fire({
            icon: 'error',
            title: title,
            text: text,
            ...options
        });
    },
    
    /**
     * Alerta de advertencia
     */
    warning(title, text = '', options = {}) {
        return CustomSwal.fire({
            icon: 'warning',
            title: title,
            text: text,
            ...options
        });
    },
    
    /**
     * Alerta de información
     */
    info(title, text = '', options = {}) {
        return CustomSwal.fire({
            icon: 'info',
            title: title,
            text: text,
            ...options
        });
    },
    
    /**
     * Confirmación con botones Sí/No
     */
    confirm(title, text = '', options = {}) {
        return CustomSwal.fire({
            icon: 'question',
            title: title,
            text: text,
            showCancelButton: true,
            confirmButtonText: options.confirmText || 'Sí',
            cancelButtonText: options.cancelText || 'No',
            reverseButtons: true,
            ...options
        });
    },
    
    /**
     * Confirmación de eliminación
     */
    confirmDelete(itemName = 'este elemento', options = {}) {
        return CustomSwal.fire({
            icon: 'warning',
            title: '¿Estás seguro?',
            text: `¿Deseas eliminar ${itemName}? Esta acción no se puede deshacer.`,
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc3545',
            reverseButtons: true,
            ...options
        });
    },
    
    /**
     * Toast notification (notificación pequeña)
     */
    toast(title, icon = 'success', options = {}) {
        const Toast = Swal.mixin({
            toast: true,
            position: options.position || 'top-end',
            showConfirmButton: false,
            timer: options.timer || 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
        
        return Toast.fire({
            icon: icon,
            title: title,
            ...options
        });
    },
    
    /**
     * Loading/Procesando
     */
    loading(title = 'Procesando...', text = 'Por favor, espere...') {
        return Swal.fire({
            title: title,
            text: text,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
    },
    
    /**
     * Cerrar cualquier modal abierto
     */
    close() {
        Swal.close();
    },
    
    /**
     * Input prompt
     */
    async prompt(title, inputOptions = {}) {
        const defaultInputOptions = {
            inputPlaceholder: 'Ingrese el valor aquí...',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Aceptar',
            cancelButtonText: 'Cancelar',
            showLoaderOnConfirm: true,
            ...inputOptions
        };
        
        return CustomSwal.fire({
            title: title,
            input: inputOptions.type || 'text',
            ...defaultInputOptions
        });
    },
    
    /**
     * Formulario personalizado con HTML
     */
    customForm(title, html, options = {}) {
        return CustomSwal.fire({
            title: title,
            html: html,
            focusConfirm: false,
            showCancelButton: true,
            ...options
        });
    }
};

// Hacer disponible globalmente para compatibilidad
window.Swal = Swal;
window.AlertManager = AlertManager;

// Exportar para uso modular
export default CustomSwal;
export { Swal };
